import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Code2, Eye, FileText, Mail, Plus, Save, Send, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { EmailPreviewFrame } from "@/components/admin/EmailPreviewFrame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyDraft = {
  id: "",
  name: "",
  subject: "",
  heading: "",
  body_html: "",
  status: "draft",
};

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });
const draftIdentity = (draft) => draft?.id || "";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const NewsletterDraftsManagement = ({ token }) => {
  const [drafts, setDrafts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedListId, setSelectedListId] = useState("");
  const [form, setForm] = useState(emptyDraft);
  const [editorMode, setEditorMode] = useState("rich");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const selectedDraft = useMemo(
    () => drafts.find((draft) => draftIdentity(draft) === selectedId),
    [drafts, selectedId],
  );
  const adminLists = useMemo(
    () => lists.filter((list) => (list.owner_type || "admin") === "admin"),
    [lists],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeaders(token);
      const [draftResponse, templateResponse, listResponse] = await Promise.all([
        axios.get(`${API}/admin/newsletter-drafts`, { headers }),
        axios.get(`${API}/admin/system-emails`, { headers }),
        axios.get(`${API}/admin/email-lists`, { headers }),
      ]);
      const nextDrafts = draftResponse.data || [];
      const nextLists = (listResponse.data || []).filter((list) => (list.owner_type || "admin") === "admin");
      setDrafts(nextDrafts);
      setTemplates(templateResponse.data || []);
      setLists(listResponse.data || []);
      setSelectedListId((current) => current || nextLists[0]?.id || "");
      setSelectedId((current) => {
        const selected = nextDrafts.find((draft) => draft.id === current) || nextDrafts[0];
        if (selected) {
          setForm({ ...emptyDraft, ...selected });
          return selected.id;
        }
        setForm(emptyDraft);
        return "";
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load newsletter drafts");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedDraft) setForm({ ...emptyDraft, ...selectedDraft });
  }, [selectedDraft]);

  const handleNew = () => {
    setSelectedId("");
    setSelectedTemplateId("");
    setForm(emptyDraft);
    setEditorMode("rich");
  };

  const loadTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find((item) => (item.id || item.key) === templateId);
    if (!template) return;
    setSelectedId("");
    setForm({
      ...emptyDraft,
      name: `${template.name} Newsletter`,
      subject: template.subject || "",
      heading: template.heading || "",
      body_html: template.body_html || "",
    });
  };

  const saveDraft = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) {
      toast.error("Draft name, subject, and body are required");
      return null;
    }
    setBusy(true);
    try {
      const headers = authHeaders(token);
      const payload = {
        name: form.name,
        subject: form.subject,
        heading: form.heading,
        body_html: form.body_html,
        status: form.status || "draft",
      };
      let response;
      if (form.id) {
        response = await axios.put(`${API}/admin/newsletter-drafts/${form.id}`, payload, { headers });
        setDrafts((previous) => previous.map((draft) => draft.id === form.id ? response.data : draft));
      } else {
        response = await axios.post(`${API}/admin/newsletter-drafts`, payload, { headers });
        setDrafts((previous) => [response.data, ...previous]);
        setSelectedId(response.data.id);
      }
      setForm({ ...emptyDraft, ...response.data });
      toast.success("Newsletter draft saved");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save newsletter draft");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async () => {
    if (!form.id) return;
    if (!window.confirm("Delete this newsletter draft?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/newsletter-drafts/${form.id}`, { headers: authHeaders(token) });
      const nextDrafts = drafts.filter((draft) => draft.id !== form.id);
      setDrafts(nextDrafts);
      setSelectedId(nextDrafts[0]?.id || "");
      setForm(nextDrafts[0] ? { ...emptyDraft, ...nextDrafts[0] } : emptyDraft);
      toast.success("Newsletter draft deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete newsletter draft");
    } finally {
      setBusy(false);
    }
  };

  const sendDraft = async () => {
    if (!form.id) {
      toast.error("Save the newsletter draft before sending");
      return;
    }
    if (!selectedListId) {
      toast.error("Choose an admin email list");
      return;
    }
    setBusy(true);
    try {
      const response = await axios.post(`${API}/admin/newsletter-drafts/${form.id}/send`, {
        list_id: selectedListId,
      }, { headers: authHeaders(token) });
      toast.success(`Newsletter sent: ${response.data.success_count || 0}/${response.data.recipient_count || 0} delivered`);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-gray-100 bg-white p-6 text-sm text-gray-500">Loading newsletter drafts...</div>;
  }

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[20rem_minmax(0,1fr)]">
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Newsletter Drafts</h2>
          <Button type="button" size="sm" onClick={handleNew} className="rounded-full bg-[#1a1a1a] px-4 text-white" data-testid="add-newsletter-draft-btn">
            <Plus className="mr-2 h-4 w-4" /> New
          </Button>
        </div>

        <div className="mb-4">
          <Label>Load a template</Label>
          <select
            value={selectedTemplateId}
            onChange={(event) => loadTemplate(event.target.value)}
            className="mt-2 h-10 w-full rounded-full border border-gray-200 bg-white px-3 text-sm"
            data-testid="newsletter-template-select"
          >
            <option value="">Choose template</option>
            {templates.map((template) => (
              <option key={template.id || template.key} value={template.id || template.key}>{template.name}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          {drafts.length === 0 ? (
            <p className="rounded-xl bg-gray-50 p-3 text-xs text-gray-500">No newsletter drafts yet.</p>
          ) : drafts.map((draft) => (
            <button
              key={draft.id}
              type="button"
              onClick={() => setSelectedId(draft.id)}
              className={`w-full rounded-xl border p-3 text-left transition-colors ${
                selectedId === draft.id ? "border-[#42D78A] bg-[#42D78A]/10" : "border-gray-100 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-start gap-2">
                <FileText className="mt-0.5 h-4 w-4 text-gray-400" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-[#1a1a1a]">{draft.name}</p>
                  <p className="mt-1 truncate text-xs text-gray-500">{draft.subject}</p>
                  {draft.last_sent_at && <p className="mt-1 text-xs text-gray-400">Sent {formatDate(draft.last_sent_at)}</p>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.8fr)]">
        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {form.id ? "Edit Newsletter" : "Create Newsletter"}
              </h2>
              <p className="mt-1 text-sm text-gray-500">Send announcements to admin email lists only.</p>
            </div>
            <Badge className="w-fit bg-gray-100 text-gray-700">{form.status || "draft"}</Badge>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label>Draft Name</Label>
              <Input value={form.name} onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))} className="mt-2" data-testid="newsletter-draft-name" />
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(event) => setForm((previous) => ({ ...previous, subject: event.target.value }))} className="mt-2" maxLength={180} data-testid="newsletter-draft-subject" />
            </div>
            <div className="md:col-span-2">
              <Label>Heading</Label>
              <Input value={form.heading} onChange={(event) => setForm((previous) => ({ ...previous, heading: event.target.value }))} className="mt-2" data-testid="newsletter-draft-heading" />
            </div>
            <div className="md:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label>Email Body</Label>
                <div className="flex rounded-full bg-gray-100 p-1">
                  {[
                    { value: "rich", label: "Rich Text", icon: Mail },
                    { value: "html", label: "HTML", icon: Code2 },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setEditorMode(mode.value)}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                        editorMode === mode.value ? "bg-[#1a1a1a] text-white" : "text-gray-600 hover:text-[#1a1a1a]"
                      }`}
                      data-testid={`newsletter-mode-${mode.value}`}
                    >
                      <mode.icon className="h-3.5 w-3.5" /> {mode.label}
                    </button>
                  ))}
                </div>
              </div>
              {editorMode === "html" ? (
                <Textarea
                  value={form.body_html}
                  onChange={(event) => setForm((previous) => ({ ...previous, body_html: event.target.value }))}
                  className="mt-2 min-h-[360px] font-mono text-xs"
                  spellCheck={false}
                  data-testid="newsletter-body-html"
                />
              ) : (
                <div className="mt-2">
                  <RichTextEditor
                    key={form.id || "new-newsletter-draft"}
                    value={form.body_html}
                    onChange={(value) => setForm((previous) => ({ ...previous, body_html: value }))}
                    testId="newsletter-body-rich"
                    imageUploadEndpoint="/api/upload/message-image"
                    videoUploadEndpoint="/api/upload/message-video"
                    token={token}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <Label>Send to admin list</Label>
            <div className="mt-2 flex flex-col gap-3">
              <select
                value={selectedListId}
                onChange={(event) => setSelectedListId(event.target.value)}
                className="h-11 rounded-full border border-gray-200 bg-white px-4 text-sm"
                data-testid="newsletter-list-select"
              >
                <option value="">Choose an admin list</option>
                {adminLists.map((list) => (
                  <option key={list.id} value={list.id}>{list.name} ({list.active_member_count || 0})</option>
                ))}
              </select>
              <Button onClick={sendDraft} disabled={busy || !form.id || !selectedListId} className="w-full rounded-full bg-[#42D78A] px-6 font-bold text-[#1a1a1a] hover:bg-[#3bc27c] sm:w-fit" data-testid="send-newsletter-btn">
                <Send className="mr-2 h-4 w-4" /> Send
              </Button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={saveDraft} disabled={busy} className="rounded-full bg-[#42D78A] px-6 font-bold text-[#1a1a1a] hover:bg-[#3bc27c]" data-testid="save-newsletter-draft-btn">
              <Save className="mr-2 h-4 w-4" /> {busy ? "Saving..." : "Save Draft"}
            </Button>
            {form.id && (
              <Button onClick={deleteDraft} disabled={busy} variant="outline" className="rounded-full text-red-600 hover:bg-red-50">
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#42D78A]" />
            <div>
              <h3 className="text-lg font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>Preview</h3>
              <p className="text-xs text-gray-500">Rendered with sample variables and the 614 Shop Local email wrapper.</p>
            </div>
          </div>
          <EmailPreviewFrame
            subject={form.subject}
            heading={form.heading}
            bodyHtml={form.body_html}
            includeMarketingFooter
            includeAutomatedFooter={false}
            testId="newsletter-preview"
          />
        </div>
      </div>
    </div>
  );
};
